import { useCallback } from "react";
import { Box } from "~/components/Box";
import { EmptyState } from "~/components/EmptyState";
import { Grid } from "~/components/Grid";
import { LazyList } from "~/components/LazyList";
import { Skeleton } from "~/components/Skeleton";
import { TopViolationItem, TopViolationsResponse } from "../types";
import { asCount } from "../utils";
import { TableBodyCell, TableHeaderCell, TableRow } from "./dataTable";
import { PaginatedTableCard } from "./PaginatedTableCard";
import { useClientTable } from "./useClientTable";

export interface TopViolationsTableProps {
  title: string;
  data?: TopViolationsResponse;
  isFetching: boolean;
}

const PAGE_SIZE = 10;

// `#` rank + `name` + `violations`.
const columnsTemplate = "56px minmax(160px, 2fr) minmax(110px, 1fr)";

const searchSelector = (item: TopViolationItem): string => item.name;

export const TopViolationsTable = ({ title, data, isFetching }: TopViolationsTableProps) => {
  const ranking = data?.ranking ?? [];

  const { pageRows, search, setSearch, page, setPage, totalPages, filteredCount } = useClientTable(
    ranking,
    { searchSelector, pageSize: PAGE_SIZE },
  );

  const renderBody = useCallback(() => {
    return (
      <Box css={{ overflowX: "auto" }}>
        <Grid style={{ gridTemplateColumns: columnsTemplate }}>
          <TableHeaderCell>#</TableHeaderCell>
          <TableHeaderCell>Nome</TableHeaderCell>
          <TableHeaderCell numeric>Violações</TableHeaderCell>

          <LazyList
            as="div"
            itemAs="div"
            css={{ display: "contents", "& > div": { display: "contents" } }}
            isLoading={isFetching || !data}
            items={pageRows}
            skeletonQuantity={10}
            EmptyComponent={
              <div style={{ gridColumn: "1 / -1" }}>
                <EmptyState>Nenhuma violação encontrada para o período.</EmptyState>
              </div>
            }
            SkeletonComponent={
              <TableRow>
                <TableBodyCell muted>
                  <Skeleton css={{ width: "20px", height: "$5" }} />
                </TableBodyCell>
                <TableBodyCell bold>
                  <Skeleton css={{ width: "70%", height: "$5" }} />
                </TableBodyCell>
                <TableBodyCell numeric>
                  <Skeleton css={{ width: "50px", height: "$5" }} />
                </TableBodyCell>
              </TableRow>
            }
            render={(item, index) => (
              <TableRow key={item.uuid}>
                <TableBodyCell muted>{(page - 1) * PAGE_SIZE + index + 1}</TableBodyCell>
                <TableBodyCell bold>{item.name}</TableBodyCell>
                <TableBodyCell numeric>{asCount(item.totalViolations)}</TableBodyCell>
              </TableRow>
            )}
          />
        </Grid>
      </Box>
    );
  }, [isFetching, data, filteredCount, pageRows, page]);

  return (
    <PaginatedTableCard
      title={title}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Buscar por nome..."
      page={page}
      totalPages={totalPages}
      onPageChange={setPage}
      hasData={!!data}
    >
      {renderBody()}
    </PaginatedTableCard>
  );
};

TopViolationsTable.displayName = "TopViolationsTable";
